import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  HeartPulse,
  Database,
  HardDrive,
  Wifi,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Server,
} from 'lucide-react';
import { systemCheckApi, systemHealthApi } from '@/api/endpoints';
import { gatewayClient } from '@/api/gatewayClient';
import { toast } from '@/utils/toast';

interface CheckResult {
  type: string;
  passed: boolean;
  message: string;
  details?: unknown;
}

interface HealthStatus {
  status: string;
  uptime: number;
  version: string;
  database: { status: string; latency_ms: number };
  cache: { status: string; hit_rate: number };
  storage: { status: string; used_bytes: number; total_bytes: number };
}

const checkIcons: Record<string, React.ElementType> = {
  database: Database,
  cache: Wifi,
  storage: HardDrive,
  queue: Server,
};

const formatUptime = (seconds: number) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatBytes = (bytes: number) => {
  if (bytes > 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes > 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e3).toFixed(1)} KB`;
};

export const SystemHealthPage = () => {
  const [checkResults, setCheckResults] = useState<CheckResult[]>([]);
  const [runningCheck, setRunningCheck] = useState<string | null>(null);

  const { data: healthRes, isLoading: healthLoading } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemHealthApi.getStatus(),
    staleTime: 30000,
  });

  const health: HealthStatus = (healthRes?.data?.data || {}) as HealthStatus;

  // Gateway health check
  const { data: gatewayRes, isLoading: gatewayLoading } = useQuery({
    queryKey: ['gateway-health'],
    queryFn: async () => {
      const start = Date.now();
      const res = await gatewayClient.get('/health');
      return { ...res, latency: Date.now() - start };
    },
    staleTime: 30000,
    retry: 1,
  });

  const gatewayHealthy = !!gatewayRes?.data;
  const gatewayLatency = gatewayRes?.latency;

  const runAllMutation = useMutation({
    mutationFn: () => systemCheckApi.runAllChecks(),
    onSuccess: (res) => {
      const results = (res?.data?.data as any) || [];
      setCheckResults(Array.isArray(results) ? results : []);
      const passed = Array.isArray(results) ? results.filter((r: CheckResult) => r.passed).length : 0;
      const total = Array.isArray(results) ? results.length : 0;
      toast.success(`${passed}/${total} checks passed`);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to run checks'),
  });

  const handleRunSingleCheck = async (checkType: string) => {
    setRunningCheck(checkType);
    try {
      const res = await systemCheckApi.runCheck(checkType);
      const result = res?.data?.data as any;
      setCheckResults((prev) => {
        const filtered = prev.filter((r) => r.type !== checkType);
        return [...filtered, { type: checkType, passed: result?.passed, message: result?.message, details: result?.details }];
      });
      toast.success(result?.passed ? `${checkType} check passed` : `${checkType} check failed`);
    } catch {
      toast.error(`${checkType} check failed`);
    } finally {
      setRunningCheck(null);
    }
  };

  const checkTypes = ['database', 'cache', 'storage', 'queue'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">System Health</h1>
          <p className="text-sm text-white/50 mt-1">Monitor system status and run diagnostics</p>
        </div>
        <button
          onClick={() => runAllMutation.mutate()}
          disabled={runAllMutation.isPending}
          className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${runAllMutation.isPending ? 'animate-spin' : ''}`} />
          <span>{runAllMutation.isPending ? 'Running...' : 'Run All Checks'}</span>
        </button>
      </div>

      {/* Health overview */}
      {!healthLoading && health.status && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-dark p-4">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${health.status === 'healthy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                <HeartPulse className={`w-5 h-5 ${health.status === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`} />
              </div>
              <div>
                <p className="text-xs text-white/50">Status</p>
                <p className={`text-lg font-semibold capitalize ${health.status === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`}>{health.status}</p>
              </div>
            </div>
          </div>
          <div className="card-dark p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <p className="text-xs text-white/50">Uptime</p>
                <p className="text-lg font-semibold text-white/90">{health.uptime ? formatUptime(health.uptime) : '—'}</p>
              </div>
            </div>
          </div>
          <div className="card-dark p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <Server className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-xs text-white/50">Version</p>
                <p className="text-lg font-semibold text-white/90">{health.version || '—'}</p>
              </div>
            </div>
          </div>
          <div className="card-dark p-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-white/50">DB Latency</p>
                <p className="text-lg font-semibold text-white/90">{health.database?.latency_ms != null ? `${health.database.latency_ms}ms` : '—'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component status cards */}
      {!healthLoading && health.status && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {health.database && (
            <div className="card-dark p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Database className="w-5 h-5 text-teal-400" />
                  <h3 className="text-sm font-medium text-white/90">Database</h3>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${health.database.status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {health.database.status}
                </span>
              </div>
              <p className="text-xs text-white/50">Latency: {health.database.latency_ms}ms</p>
            </div>
          )}
          {health.cache && (
            <div className="card-dark p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <Wifi className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-sm font-medium text-white/90">Cache</h3>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${health.cache.status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {health.cache.status}
                </span>
              </div>
              <p className="text-xs text-white/50">Hit Rate: {(health.cache.hit_rate * 100).toFixed(1)}%</p>
            </div>
          )}
          {health.storage && (
            <div className="card-dark p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <HardDrive className="w-5 h-5 text-purple-400" />
                  <h3 className="text-sm font-medium text-white/90">Storage</h3>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${health.storage.status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {health.storage.status}
                </span>
              </div>
              <p className="text-xs text-white/50">
                {formatBytes(health.storage.used_bytes)} / {formatBytes(health.storage.total_bytes)}
              </p>
              <div className="w-full h-2 bg-white/[0.06] rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full"
                  style={{ width: `${health.storage.total_bytes > 0 ? (health.storage.used_bytes / health.storage.total_bytes) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gateway health */}
      <div className="card-dark p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Server className="w-5 h-5 text-indigo-400" />
            <h3 className="text-sm font-medium text-white/90">Nexus Gateway</h3>
          </div>
          {gatewayLoading ? (
            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400">checking...</span>
          ) : (
            <span className={`px-2 py-1 rounded text-xs font-medium ${gatewayHealthy ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
              {gatewayHealthy ? 'healthy' : 'unreachable'}
            </span>
          )}
        </div>
        <p className="text-xs text-white/50">
          {gatewayLatency != null ? `Latency: ${gatewayLatency}ms` : 'Routes to CertChain & exam backend'}
        </p>
      </div>

      {/* Diagnostic checks */}
      <div className="card-dark overflow-hidden">
        <div className="p-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-medium text-white/90">Diagnostic Checks</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {checkTypes.map((type) => {
            const Icon = checkIcons[type] || Server;
            const result = checkResults.find((r) => r.type === type);
            const isRunning = runningCheck === type;

            return (
              <div key={type} className="flex items-center justify-between p-4 bg-white/[0.02] rounded-lg">
                <div className="flex items-center space-x-3">
                  <Icon className="w-5 h-5 text-white/50" />
                  <div>
                    <p className="text-sm font-medium text-white/90 capitalize">{type}</p>
                    {result && (
                      <div className="flex items-center space-x-2 mt-1">
                        {result.passed ? (
                          <CheckCircle className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <XCircle className="w-3 h-3 text-red-400" />
                        )}
                        <span className="text-xs text-white/60">{result.message}</span>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRunSingleCheck(type)}
                  disabled={isRunning}
                  className="btn-outline px-3 py-1.5 rounded-lg text-xs flex items-center space-x-1"
                >
                  <RefreshCw className={`w-3 h-3 ${isRunning ? 'animate-spin' : ''}`} />
                  <span>{isRunning ? 'Running...' : 'Run'}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
